import type {
	CharlieTableHook,
	CharlieTableRow,
	FinalCharlieTableRow,
	RowStruct,
} from '../use-charlie-table';

/**
 * Get the row structs for a charlie table row. Heavily inspired by TanStacks CoreRowModel.
 * @param row A charlie table row
 */
export const getRowStructs = <TableDataModel extends object>(
	options: Partial<CharlieTableHook<TableDataModel>>,
): RowStruct<TableDataModel> => {
	const rowStruct: RowStruct<TableDataModel> = {
		rows: [],
		flatRows: [],
		rowsById: {},
	};

	const recurseRows = ({
		originalRows,
		depth = 0,
		parent,
	}: {
		originalRows: Array<CharlieTableRow<TableDataModel>>;
		depth?: number;
		parent?: FinalCharlieTableRow<TableDataModel>;
	}) => {
		const rows: Array<FinalCharlieTableRow<TableDataModel>> = [];
		originalRows.forEach((row, index) => {
			const finalRow: FinalCharlieTableRow<TableDataModel> = {
				...row,
				depth,
				index,
				parent,
				id:
					options.getRowId?.({ row, index, parent }) ??
					`${parent ? [parent.id, index].join('.') : index}`,
				subRows: undefined,
			};
			rowStruct.flatRows.push(finalRow);
			rowStruct.rowsById[finalRow.id] = finalRow;
			rows.push(finalRow);
			if (options.getChildRows) {
				const originalSubRows = options.getChildRows(row);
				if (originalSubRows?.length) {
					finalRow.subRows = recurseRows({
						originalRows: originalSubRows,
						depth: depth + 1,
						parent: finalRow,
					});
				}
			}
		});
		return rows;
	};
	if (options.rows == null) {
		return rowStruct;
	}

	rowStruct.rows = recurseRows({ originalRows: options.rows });

	const expanded: Array<FinalCharlieTableRow<TableDataModel>> = [];

	const recurseExpanded = (rows: Array<FinalCharlieTableRow<TableDataModel>>) => {
		rows.forEach((row) => {
			expanded.push(row);
			if (row.subRows?.length && options.state?.expanded?.[row.id] === true) {
				recurseExpanded(row.subRows);
			}
		});
	};
	if (options.getChildRows) {
		recurseExpanded(rowStruct.rows);
		rowStruct.rows = expanded;
	}

	return rowStruct;
};
